import { DataTable, FormSectionHeader, Loader } from "@/components";
import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui";
import { activeLevels, cn } from "@/lib/utils";
import { CircleCheck, LockKeyhole } from "lucide-react";
import ConfigureDialog from "./ConfigureDialog/ConfigureDialog";
import useDistributionRulesByLevel from "./useDistributionRulesByLevel";

const DistributionRulesByLevel = () => {
  const {
    columns,
    tableData,
    isLoadingRules,
    isLoadingFinancialInfo,
    currentReportingLevelLabel,
    activeThresholdIndex,
    onLevelChange,
    openConfigureDialog,
    setOpenConfigureDialog,
    configuredData,
    activeStatusTab,
    activeLevel,
    onStatusTabChange,
    activeLevelIndex,
    ruleStatusTabs
  } = useDistributionRulesByLevel();

  return isLoadingFinancialInfo ? <Loader /> : (
    <>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {activeLevels.map((item: any, index: number) => {
          const isUnlocked = index <= activeThresholdIndex;
          const isActive = activeLevelIndex == index;
          const isPrev = index < activeLevelIndex;

          return (
            <Button
              key={item.id}
              type="button"
              disabled={!isUnlocked}
              className={cn(
                "h-[40px] gap-1 px-3 bg-transparent shadow-none border-1 border-primary",
                // isActive && "bg-white hover:bg-white shadow-xl",
                isActive && "bg-primary hover:bg-primary text-white hover:text-white",
                !isUnlocked && "bg-gray-400 hover:bg-gray-400 border-gray-400 text-gray-800" 
              )}
              variant="outline"
              onClick={onLevelChange(item.level)}
            >
              {isUnlocked ? isPrev ? <CircleCheck className="fill-green-600 stroke-white h-6 w-6" /> : <></> : <LockKeyhole />}
              {item.label}
            </Button>
          );
        })}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,1fr)_330px] xl:grid-cols-[minmax(0,1fr)_430px]">
        <div>
          <Tabs value={activeStatusTab} onValueChange={onStatusTabChange}>
            <TabsList className="gap-x-4 bg-transparent p-0">
              {ruleStatusTabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="hover:cursor-pointer text-gray-400 hover:text-primary px-0 border-0 shadow-none data-[state=active]:rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {ruleStatusTabs.map((tab) => (
              <TabsContent key={tab.value} value={tab.value} className="w-full">
                {isLoadingRules ? <Loader /> :
                  <DataTable
                    columns={columns}
                    data={tableData}
                    className="mt-2 w-full shadow-none bg-transparent overflow-visible"
                    tableWrapperClassName="px-0 bg-white rounded-lg overflow-hidden"
                    headerClass={{
                      row: "bg-primary-300 hover:bg-primary-300",
                      cell: "h-[40px]",
                    }}
                    hideFooter
                  />  
                }
              </TabsContent>
            ))}
          </Tabs>
        </div>

        <div className="h-fit rounded-lg bg-white shadow-sm">
          <div className="p-5">
            <FormSectionHeader>Active Level Path</FormSectionHeader>
            <div className="space-y-4 text-[14px] text-medium">
              {
                activeLevels?.map((level: any, index: number) => {
                  const isUnlocked = index <= activeThresholdIndex;
                  return (
                    <div className={cn("flex items-center gap-2", isUnlocked ? "text-gray-700" : "text-gray-400")}>
                      {isUnlocked ? <CircleCheck className="h-5 w-5 fill-green-600 stroke-white" /> : <LockKeyhole className="h-5 w-5 text-primary" />}
                      <span className="font-medium">
                        Level {index + 1}: {level?.label || ""} {isUnlocked ? "" : "(DISABLED)"}
                      </span>
                    </div>
                  )
                })
              }
            </div>
            <div className="mt-5 rounded-md border border-primary bg-blue-50 px-3 py-3 text-base font-[500] leading-7 text-gray-700 text-[14px] text-medium">
              Since reporting level is {currentReportingLevelLabel}, the system will stop distribution
              at {currentReportingLevelLabel} level.
            </div>
          </div>

          <div className="border-t border-g-200 p-5">
            <FormSectionHeader>
              System Logic
            </FormSectionHeader>
            <div className="flex flex-col gap-3 leading-5 text-g-500 text-[13px] font-normal">
              <div className="flex flex-col">
                <p>Allocation follows strict hierarchy:</p>
                <span>Community → Zone → Block → Property</span>
              </div>
              <div className="my-2 gap-4 flex flex-col">
                <p >Each level must <b>CLOSE</b> before next level starts.</p>
                <p className="text-primary">
                  % = Value* 100 / Total (Auto Calculated)
                </p>
              </div>
              <p>User sees full calculation before execution.</p>
            </div>
          </div>
        </div>
      </div>

      {openConfigureDialog && <ConfigureDialog
        open={openConfigureDialog}
        setOpen={setOpenConfigureDialog}
        level={activeLevel}
        budgetType={activeStatusTab}
        configuredData={configuredData}
      />}
    </>
  );
};

export default DistributionRulesByLevel;
